import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Stack,
  Select,
  FormControl,
  MenuItem,
  Paper,
} from "@mui/material";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

import SearchIcon from "@mui/icons-material/Search";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";

import { getLocationList } from "../../../services/busServices";
import Loading from "../Loading/Loading";

export default function Hero({
  loading,
  hasSearched,
  onViewResults,
  handleSubmit,
  setPickup,
  setDrop,
  setDate,
  pickup,
  drop,
  date,
}) {
  const [allLocations, setAllLocations] = useState({});

  useEffect(() => {
    getLocationList()
      .then(setAllLocations)
      .catch((err) => console.error("Error fetching locations:", err.message));
  }, []);

  return loading ? (
    <Loading />
  ) : (
    <Box
      id="hero"
      sx={{
        minHeight: hasSearched ? { xs: "auto", md: "78vh" } : "100vh",
        background:
          "linear-gradient(150deg, #0D1B2A 0%, #1565C0 55%, #0288D1 100%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        pt: 10,
        pb: hasSearched ? 5 : 8,
        position: "relative",
        overflow: "hidden",
        transition: "min-height 0.25s ease, padding 0.25s ease",
      }}
    >
      {/* Decorative blobs */}
      <Box
        sx={{
          position: "absolute",
          top: -120,
          right: -120,
          width: 420,
          height: 420,
          borderRadius: "50%",
          bgcolor: "rgba(255,255,255,0.05)",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -160,
          left: -80,
          width: 520,
          height: 520,
          borderRadius: "50%",
          bgcolor: "rgba(255,255,255,0.04)",
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="md" sx={{ position: "relative", zIndex: 1, textAlign: "center" }}>
        {/* Headline */}
        <Typography
          variant="h2"
          fontWeight={900}
          color="white"
          sx={{
            mb: 1.5,
            fontSize: { xs: "2rem", sm: "2.8rem", md: "3.4rem" },
            letterSpacing: -1,
            lineHeight: 1.1,
          }}
        >
          Your Journey Starts Here
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: "rgba(255,255,255,0.72)",
            mb: hasSearched ? 4 : 6,
            fontWeight: 400,
            fontSize: { xs: "1rem", md: "1.15rem" },
          }}
        >
          Book intercity bus tickets  fast, easy, and secure
        </Typography>

        {/* Search card */}
        <Paper
          component="form"
          onSubmit={handleSubmit}
          elevation={24}
          sx={{
            borderRadius: 4,
            p: { xs: 3, sm: 4 },
            bgcolor: "rgba(255,255,255,0.98)",
          }}
        >
          <Grid container spacing={2} alignItems="flex-end">
            {/* Pickup */}
            <Grid item xs={12} sm={6} md={3.5}>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.8 }}
              >
                <LocationOnIcon sx={{ fontSize: 14 }} /> FROM
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={pickup}
                  onChange={(e) => setPickup(e.target.value)}
                  displayEmpty
                  sx={{
                    borderRadius: 2,
                    bgcolor: "#F8FAFF",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(0,0,0,0.12)",
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    Select pickup point
                  </MenuItem>
                  {Object.keys(allLocations).map((loc) => (
                    <MenuItem key={loc} value={loc}>
                      {loc}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Drop */}
            <Grid item xs={12} sm={6} md={3.5}>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.8 }}
              >
                <LocationOnIcon sx={{ fontSize: 14 }} /> TO
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={drop}
                  onChange={(e) => setDrop(e.target.value)}
                  displayEmpty
                  sx={{
                    borderRadius: 2,
                    bgcolor: "#F8FAFF",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(0,0,0,0.12)",
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    Select drop point
                  </MenuItem>
                  {Object.keys(allLocations).map((loc) => (
                    <MenuItem key={loc} value={loc}>
                      {loc}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Date */}
            <Grid item xs={12} sm={6} md={3}>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.8 }}
              >
                <CalendarMonthIcon sx={{ fontSize: 14 }} /> DATE
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  minDate={dayjs()}
                  maxDate={dayjs().add(14, "day")}
                  value={date}
                  onChange={(newValue) => setDate(newValue)}
                  slotProps={{
                    textField: {
                      readOnly: true,
                      onPaste: (e) => e.preventDefault(),
                    },
                  }}
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: "#F8FAFF",
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            {/* Search button */}
            <Grid item xs={12} sm={6} md={2}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                startIcon={<SearchIcon />}
                sx={{
                  borderRadius: 2,
                  py: 1.85,
                  fontWeight: 700,
                  fontSize: "1rem",
                  background:
                    "linear-gradient(135deg, #1565C0 0%, #0288D1 100%)",
                  boxShadow: "0 4px 20px rgba(21,101,192,0.45)",
                  "&:hover": {
                    boxShadow: "0 8px 28px rgba(21,101,192,0.55)",
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {hasSearched && (
          <Paper
            elevation={0}
            sx={{
              mt: 2,
              px: 2.5,
              py: 1.5,
              borderRadius: 999,
              bgcolor: "rgba(255,255,255,0.14)",
              border: "1px solid rgba(255,255,255,0.18)",
              backdropFilter: "blur(14px)",
              display: "inline-flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Typography variant="body2" sx={{ color: "white", fontWeight: 600 }}>
              Search results are ready below
            </Typography>
            <Button
              variant="text"
              onClick={onViewResults}
              endIcon={<KeyboardDoubleArrowDownIcon />}
              sx={{
                color: "white",
                fontWeight: 700,
                minWidth: "auto",
                px: 0,
                "&:hover": { bgcolor: "transparent", opacity: 0.8 },
              }}
            >
              View results
            </Button>
          </Paper>
        )}

        {/* Trust badges */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1, sm: 3 }}
          justifyContent="center"
          alignItems="center"
          sx={{ mt: 5 }}
        >
          {["100% Secure Payments", "Instant Confirmation", "Easy Cancellation"].map(
            (badge) => (
              <Typography
                key={badge}
                variant="body2"
                sx={{
                  color: "rgba(255,255,255,0.65)",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                 {badge}
              </Typography>
            )
          )}
        </Stack>
      </Container>
    </Box>
  );
}
